import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { DatePicker, Input, Label, TimePicker } from "@src/common/components";
import {
  useOverridePodMetadataMutation,
  Shipment,
} from "@api/graphql/generated/generated-types";
import CourieButton from "@src/common/components/Button/Button";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useAuth } from "@src/common/hooks/useAuth";
import {
  momentToTimePickerDate,
  makeTimeOnDateInCourierTZ,
  dateToTimeString,
  timeStringToDate,
} from "@src/common/utils/timezoneConverters";

type FormData = {
  signedBy: string;
  deliveredAt: Date | null;
  deliveryTime: Date | null;
};

interface OverridePODTabProps {
  selectedShipment: Shipment | undefined;
  onClose: () => void;
}

function OverridePODTab({ selectedShipment, onClose }: OverridePODTabProps) {
  const { courierId, courieUser, employeeId } = useAuth();
  const { handleSubmit, setValue, control } = useForm<FormData>();
  const [overridePodMetadataMutation, { loading }] =
    useOverridePodMetadataMutation();
  const { showToast } = useCourieStore();

  useEffect(() => {
    setValue("signedBy", selectedShipment?.signedByOverride || "");
    if (selectedShipment?.deliveredAtOverride) {
      // Convert unix timestamp to moment in courier TZ, then to DatePicker/TimePicker Date
      const deliveredAtMoment = moment.unix(
        selectedShipment.deliveredAtOverride
      );
      const deliveredAtDate = momentToTimePickerDate(deliveredAtMoment);
      setValue("deliveredAt", deliveredAtDate);
      setValue("deliveryTime", deliveredAtDate);
    } else {
      setValue("deliveredAt", null);
      setValue("deliveryTime", null);
    }
  }, [selectedShipment, setValue]);

  const onSubmit = handleSubmit(({ signedBy, deliveredAt, deliveryTime }) => {
    if (!courierId) return;

    // Combine date and time and convert to unix timestamp in courier TZ
    const deliveredAtUnix =
      deliveredAt && deliveryTime
        ? makeTimeOnDateInCourierTZ(deliveryTime, deliveredAt)
        : null;

    const variables = {
      courierId: courierId,
      shipmentId: selectedShipment?.id || "",
      updatedBy: employeeId ? employeeId : courieUser!.id,
      signedBy: signedBy || null,
      deliveredAt: deliveredAtUnix,
    };

    overridePodMetadataMutation({
      variables,
    })
      .then(() => {
        showToast({
          type: "success",
          message: "POD metadata updated successfully",
        });
        onClose();
      })
      .catch((error) => {
        showErrorToast(error, showToast);
      });
  });

  return (
    <form onSubmit={onSubmit}>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="flex flex-col">
          <Label>Signed By</Label>
          <Controller
            control={control}
            name="signedBy"
            render={({ field }) => <Input {...field} size="mini" />}
          />
        </div>
        <div className="flex flex-col z-50">
          <Label>Delivered At</Label>
          <Controller
            control={control}
            name="deliveredAt"
            render={({ field }) => (
              <DatePicker
                size="mini"
                value={field.value}
                formatString="MMM do, yyyy"
                onChange={({ date }) => field.onChange(date)}
              />
            )}
          />
        </div>
        <div className="flex flex-col z-50">
          <Label>Delivery Time</Label>
          <Controller
            control={control}
            name="deliveryTime"
            render={({ field }) => (
              <TimePicker
                size="mini"
                nullable
                value={dateToTimeString(field.value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value === "") {
                    field.onChange(null);
                  } else {
                    field.onChange(timeStringToDate(event.target.value));
                  }
                }}
              />
            )}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <CourieButton onClick={onClose} color="secondary" type="button">
          Cancel
        </CourieButton>
        <CourieButton type="submit" disabled={loading} isProcessing={loading}>
          Confirm
        </CourieButton>
      </div>
    </form>
  );
}

export { OverridePODTab };
export type { OverridePODTabProps };
